import * as React from "react";
import { Button, notification } from "antd";
import { v4 as uuidv4 } from "uuid";
import {
  formInitialize,
  formSet,
  formUninitialize,
  useCpFormAnyValueId,
  useCpFormSubmit,
} from "../../gvc/testNewForm/utils";
import { CpFormContext } from "../../../components/form/CpFormContext";
import { CpFormValidator } from "../../gvc/testNewForm/CpFormValidator";
import { CpDebugModal } from "../../workload/draft/debugModal";
import { getDefaultVolumeSetInput, inputToVolumeSet } from "../utils";
import { VolumeSetInput } from "../../../schema/types/volumeSet/input";
import { VolumeSet } from "../../../schema/types/volumeSet/entity";
import { getValidator } from "./validator";
import { parentLink, request, resourceLink } from "../../../services/cpln";
import { IS_DEPLOYMENT_ENV_PROD } from "../../../envVariables";
import { CpTagsNew } from "../../../components/form/CpTagsNew";
import { General } from "./general";
import { Spec } from "../spec/spec";
import { FileSystemType } from "../spec/fileSystemType";
import { store } from "../../../store";
import { parseLink } from "../../../utils/linkParser/linkParser";
import { KindSelectState } from "../../../components/antd/kindselect/KindSelect";

interface EmbeddedVolumeSetCreateProps {
  onDone: (itemLink: string, item: VolumeSet | undefined) => void | Promise<void>;
  onCancel: () => void;
  lockedFileSystemType?: FileSystemType;
  lockedGvc?: KindSelectState;
}

export const EmbeddedVolumeSetCreate: React.FC<EmbeddedVolumeSetCreateProps> = ({
  onDone,
  onCancel,
  lockedFileSystemType,
  lockedGvc,
}) => {
  const formIdRef = React.useRef(uuidv4());
  const formId = formIdRef.current;

  const [isInitialized, setIsInitialized] = React.useState(false);

  React.useEffect(() => {
    const input = getDefaultVolumeSetInput();
    if (lockedFileSystemType) {
      input.fileSystemType = lockedFileSystemType;
    }
    if (lockedGvc) {
      input.gvc = lockedGvc;
    }
    formInitialize(formId, input);
    setIsInitialized(true);
    return () => {
      formUninitialize(formId);
    };
  }, []);

  const submitting = useCpFormAnyValueId(formId, "meta.submitting");

  const validatorRef = React.useRef(getValidator(formId));
  const validator = validatorRef.current;

  const submit = useCpFormSubmit({
    id: formId,
    validationFn: validator,
    onSubmit: handleSubmit,
  });

  async function handleSubmit(input: VolumeSetInput) {
    try {
      const org = store.getState().context.org;
      const gvc = parseLink(input.gvc.link, { useInputCtx: true }).name;
      const ctx = { org: org, gvc: gvc };
      const body = inputToVolumeSet(input);
      await request({
        method: "post",
        url: parentLink("volumeset", ctx),
        body,
      });
      const itemLink = resourceLink("volumeset", body.name, ctx);
      notification.success({
        message: "Success",
        description: "Volume Set is created",
      });
      let createdVolumeSet: VolumeSet | undefined;
      try {
        const { data } = await request({ url: itemLink });
        createdVolumeSet = data;
      } catch (e) {}
      onDone(itemLink, createdVolumeSet);
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
    }
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <CpFormContext.Provider value={{ id: formId, submit }}>
      <CpFormValidator validationFn={validator} />
      <div className="flex flex-col gap-4">
        <General lockedGvc={lockedGvc} />
        <Spec lockedFileSystemType={lockedFileSystemType} />
        <CpTagsNew />
        <div className="flex flex-col gap-2">
          <Button type="primary" danger onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
          {!IS_DEPLOYMENT_ENV_PROD && (
            <Button type="default" onClick={() => formSet(formId, "meta.debugging", true)}>
              Debug
            </Button>
          )}
          <Button type="primary" className="action" onClick={submit} loading={submitting} disabled={submitting}>
            Create
          </Button>
        </div>
      </div>
      <CpDebugModal inputToItemFn={inputToVolumeSet} kind="volumeset" namePath="name" title="Volume Set" />
    </CpFormContext.Provider>
  );
};
